import React, { useState } from 'react';
import { FaFileExcel } from 'react-icons/fa';
import Modal from '../../../components/Utils/ModalShow';
import { apiService } from '../../../components/Config/api.service';

const AccountingJournal = ({ isOpen, onClose, selectedMonth }) => {
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportDone, setExportDone] = useState(false);
    const [exportFormat, setExportFormat] = useState('detail'); // 'detail' or 'grouped'
    const [groupedBy, setGroupedBy] = useState('account'); // 'account' | 'department' | 'assignment'
    const [includeAccountBreakdown, setIncludeAccountBreakdown] = useState(false); // new

    const handleCloseProgressModal = () => {
        setShowProgressModal(false);
        setExportDone(false);
    };

    const handleDownloadJournal = async () => {
        onClose();
        setShowProgressModal(true);
        setIsExporting(true);
        setExportDone(false);
        try {
            // map UI selection to controller's grouped_by values
            let gb = groupedBy;
            if (groupedBy === 'department' && includeAccountBreakdown) gb = 'account_department';
            if (groupedBy === 'assignment' && includeAccountBreakdown) gb = 'account_assignment';

            // include_totals should be false when includeAccountBreakdown is checked (no subtotal lines)
            const include_totals = !includeAccountBreakdown;

            const payload = {
                month: selectedMonth,
                generate_xlsx: true,
                grouped: exportFormat === 'grouped',
                grouped_by: gb,
                include_totals: include_totals
            };
            const res = await apiService.saveElement('/accounting/export', payload);
            const data = (res && res.data && (res.data.result || res.data)) ? (res.data.result || res.data) : null;
            const fileBase64 = data && (data.file || data.base64 || (data.result && data.result.file));
            const filename = (data && (data.filename || data.name)) || `Journal_Comptable_${selectedMonth}_${gb}.xlsx`;

            if (!fileBase64) throw new Error('Fichier non reçu depuis le serveur');

            const byteCharacters = atob(fileBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            apiService.downloadBlob(blob, filename);
            setExportDone(true);
        } catch (e) {
            setExportDone(false);
            console.error('Erreur lors de la récupération du journal comptable:', e);
        }
        setIsExporting(false);
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title="Confirmer le téléchargement"
                headerIcon={<FaFileExcel className="text-green-600" size={100} />}
                subtitle="Voulez-vous vraiment télécharger le journal comptable pour ce mois ?"
                showCloseButton={true}
                footer={
                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            className="px-4 py-2 rounded bg-gray-200 text-gray-700 font-medium"
                            onClick={onClose}
                        >
                            Annuler
                        </button>
                        <button
                            className="px-4 py-2 rounded bg-[--color-primary] text-white font-medium"
                            onClick={handleDownloadJournal}
                        >
                            Confirmer
                        </button>
                    </div>
                }
            >
                <div className="flex flex-col items-center justify-center py-4 px-2 space-y-4">
                    <span className="text-base text-gray-700 text-center">Cette opération va générer et télécharger le fichier Excel du journal comptable.</span>
                    
                    <div className="w-full pt-4">
                        <label className="block font-semibold mb-2 text-gray-800">Format de l'export</label>
                        <div className="flex gap-4 rounded-lg bg-gray-100 p-1">
                            <button 
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${exportFormat === 'detail' ? 'bg-white shadow-sm text-[--color-primary]' : 'text-gray-600'}`}
                                onClick={() => setExportFormat('detail')}
                            >
                                Détaillé par employé
                            </button>
                            <button 
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${exportFormat === 'grouped' ? 'bg-white shadow-sm text-[--color-primary]' : 'text-gray-600'}`}
                                onClick={() => setExportFormat('grouped')}
                            >
                                Groupé
                            </button>
                        </div>

                        {exportFormat === 'grouped' && (
                            <div className="mt-3">
                                <label className="block text-sm font-medium mb-1">Regrouper par</label>
                                <div className="flex gap-2">
                                    <button
                                        className={`px-3 py-1 rounded ${groupedBy === 'account' ? 'bg-white shadow-sm text-[--color-primary]' : 'bg-gray-50'}`}
                                        onClick={() => { setGroupedBy('account'); setIncludeAccountBreakdown(false); }}
                                    >
                                        Compte
                                    </button>
                                    <button
                                        className={`px-3 py-1 rounded ${groupedBy === 'department' ? 'bg-white shadow-sm text-[--color-primary]' : 'bg-gray-50'}`}
                                        onClick={() => setGroupedBy('department')}
                                    >
                                        Département
                                    </button>
                                    <button
                                        className={`px-3 py-1 rounded ${groupedBy === 'assignment' ? 'bg-white shadow-sm text-[--color-primary]' : 'bg-gray-50'}`}
                                        onClick={() => setGroupedBy('assignment')}
                                    >
                                        Affectation
                                    </button>
                                </div>

                                {(groupedBy === 'department' || groupedBy === 'assignment') && (
                                    <div className="mt-2 flex items-center gap-3">
                                        <input
                                            id="includeAccountBreakdown"
                                            type="checkbox"
                                            checked={includeAccountBreakdown}
                                            onChange={(e) => setIncludeAccountBreakdown(e.target.checked)}
                                        />
                                        <label htmlFor="includeAccountBreakdown" className="text-sm text-gray-700">
                                            Inclure le détail par compte (ex: Compte → Département / Affectation)
                                        </label>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={showProgressModal}
                onClose={handleCloseProgressModal}
                title={isExporting ? "Export du journal comptable" : exportDone ? "Export terminé" : "Erreur d'export"}
                headerIcon={<FaFileExcel className="text-green-600" size={28} />}
                subtitle={isExporting ? "Génération et téléchargement du fichier Excel..." : exportDone ? "Le journal comptable a été téléchargé avec succès." : ""}
                showCloseButton={!isExporting}
            >
                <div className="flex flex-col items-center justify-center py-8 px-6">
                    {isExporting ? (
                        <div className="w-10 h-10 border-4 border-green-300 border-t-green-600 rounded-full animate-spin mb-4"></div>
                    ) : exportDone ? (
                        <span className="text-lg font-semibold text-green-600">Fichier téléchargé !</span>
                    ) : (
                        <span className="text-lg font-semibold text-red-600">Erreur lors de l'export.</span>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default AccountingJournal;